// Page pour rejoindre une team via lien d'invitation
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Check, AlertCircle, Loader2, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API_URL } from '../config/api';
import { toast } from 'sonner';

export default function JoinCirclePage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, need_login
  const [circle, setCircle] = useState(null);
  const [error, setError] = useState('');

  const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token');

  useEffect(() => {
    if (!inviteCode) {
      setStatus('error');
      setError('Code d\'invitation invalide');
      return;
    }

    if (!token) {
      setStatus('need_login');
      // Store the invite code for after login
      sessionStorage.setItem('pending_circle_invite', inviteCode);
      return;
    }

    joinCircle();
  }, [inviteCode, token]);

  const joinCircle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/circles/join/${inviteCode}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();

      if (res.ok) {
        const circleData = data.circle || data;
        setCircle(circleData);
        setStatus('success');
        toast.success(`Tu as rejoint "${circleData.name}" !`);
        
        // Clear pending invite
        sessionStorage.removeItem('pending_circle_invite');
        
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/cockpit-particulier/cercles');
        }, 2000);
      } else {
        setStatus('error');
        setError(data.detail || 'Impossible de rejoindre cette team');
      }
    } catch (err) {
      setStatus('error');
      setError('Erreur de connexion au serveur');
    }
  };

  const goToLogin = () => {
    // Store invite code and redirect to login
    sessionStorage.setItem('pending_circle_invite', inviteCode);
    sessionStorage.setItem('redirect_after_login', `/rejoindre/${inviteCode}`);
    navigate('/connexion');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 size={40} className="text-orange-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Rejoindre la team...</h1>
            <p className="text-gray-500">Un instant, on vérifie ton invitation</p>
          </>
        )}

        {status === 'success' && circle && (
          <>
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold"
              style={{ backgroundColor: circle.color || '#E8650A' }}
            >
              {circle.name?.charAt(0).toUpperCase()}
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={24} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Bienvenue dans "{circle.name}" !</h1>
            <p className="text-gray-500 mb-6">
              Tu fais maintenant partie de cette team privée. Tu peux voir le feed, les tâches et les événements partagés.
            </p>
            <Button 
              className="bg-[#E8650A] hover:bg-[#d45a09] w-full"
              onClick={() => navigate('/cockpit-particulier/cercles')}
            >
              <Users size={18} className="mr-2" />
              Voir mes teams
            </Button>
          </>
        )}

        {status === 'need_login' && (
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <LogIn size={40} className="text-blue-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Connexion requise</h1>
            <p className="text-gray-500 mb-6">
              Connecte-toi ou crée un compte gratuit pour rejoindre cette team privée.
            </p>
            <div className="space-y-3">
              <Button 
                className="bg-[#E8650A] hover:bg-[#d45a09] w-full"
                onClick={goToLogin}
              >
                Se connecter
              </Button>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => {
                  sessionStorage.setItem('pending_circle_invite', inviteCode);
                  sessionStorage.setItem('redirect_after_login', `/rejoindre/${inviteCode}`);
                  navigate('/inscription');
                }}
              >
                Créer un compte gratuit
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Oups !</h1>
            <p className="text-gray-500 mb-6">{error}</p>
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
            >
              Retour à l'accueil
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
